// Transfer Dialog Types
export interface TransferItem {
  id: string;
  type: 'product' | 'box';
  barcode: string;
  name: string;
  selectedQuantity: number;
  availableQuantity: number;
  isValid: boolean;
  error?: string;
  isUnknown?: boolean;
  productId?: string;
  boxInfo?: BoxInfo;
  boxContents?: BoxProduct[];
  isExpanded?: boolean;
  isScanned?: boolean;
  isGenerated?: boolean;
  originalBoxBarcode?: string;
  modifiedAt?: Date;
  parentBoxId?: string;
}

export interface BoxProduct {
  id: string;
  productId: string;
  storageUnitId?: string;
  warehouseId?: string;
  boxRefNumber?: string;
  quantity: number;
  product?: {
    id: string;
    barcode: string;
    name: string;
    symbol?: string;
    minNumber?: number | null;
    maxNumber?: number | null;
    isSynced: boolean;
    additionalProperties?: {
      size?: string;
      Brand?: string;
      COLOR?: string;
      season?: string;
      Generic?: string;
      Category?: string;
      MainCategory?: string;
      [key: string]: any;
    } | null;
    createdAt: string;
    updatedAt: string;
    hasProperties: boolean;
    propertyCount: number;
  };
  storageUnit?: any | null;
  warehouse?: any | null;
  createdAt: string;
  updatedAt: string;
}

export interface BoxInfo {
  boxBarcode: string;
  boxRefNumber: string;
  totalQuantity: number;
  uniqueProducts: number;
  lastUpdated: string;
  isModified?: boolean;
  originalBarcode?: string;
  contentsList?: string[];
}

export interface TransferLocationOption {
  id: string;
  name: string;
  code?: string;
  type: 'StorageUnit' | 'Warehouse' | 'Client' | 'Supplier';
  warehouseName?: string;
  isRecommended?: boolean;
}

export interface InventoryDetailsResponse {
  success: boolean;
  message: string;
  data: {
    storageUnitId: string;
    storageUnitName: string;
    storageUnitCode: string;
    boxGroups: Array<{
      boxBarcode: string;
      boxRefNumber: string;
      items: BoxProduct[];
      totalQuantity: number;
      uniqueProducts: number;
      lastUpdated: string;
    }>;
    unboxedItems: BoxProduct[];
    summary: {
      totalItems: number;
      totalQuantity: number;
      uniqueProducts: number;
      boxedGroups: number;
      unboxedItems: number;
      lastActivity: string;
    };
  };
  token: string | null;
}

export interface TransferBoxBarcodeResponse {
  success: boolean;
  message: string;
  data: {
    boxBarcode: string;
  };
  token: string | null;
}

export interface GeneratedTransferBox {
  barcode: string;
  items: TransferItem[];
  description?: string;
  createdAt: Date;
  isModified?: boolean;
  originalBarcode?: string;
}

export interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (result: any) => void;
  sourceId?: string;
  sourceType?: string;
}

export interface TransferPayload {
  existingBoxBarcodes: string[];
  newBoxes: Array<{
    newBoxBarcode: string;
    items: Array<{
      itemBarcode: string;
      quantity: number;
    }>;
  }>;
  items: Array<{
    itemBarcode: string;
    quantity: number;
  }>;
  sourceType: number;
  sourceId: string;
  destinationType: number;
  destinationId: string;
  notes?: string;
  referenceId?: string;
  priority?: number;
  useBackgroundProcessing?: boolean;
  batchSize?: number;
}

export interface TransferResponse {
  success: boolean;
  message: string;
  data?: any;
  token: string | null;
}

// Validation types
export interface TransferValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ItemValidationError {
  itemId: string;
  type: 'quantity' | 'availability' | 'unknown' | 'invalid';
  message: string;
}

// Filter and search types
export type TransferFilterType = 'all' | 'product' | 'box';

export interface TransferFilters {
  type: TransferFilterType;
  searchQuery: string;
  showValidOnly: boolean;
}

// Dialog state types
export interface TransferDialogState {
  loading: boolean;
  loadingInventory: boolean;
  scanning: boolean;
  generatingBox: boolean;
  reallocatingBox: boolean;
  error: string | null;
}

// Action types for reallocate functionality
export interface ReallocateAction {
  type: 'reallocate';
  boxId: string;
  newBarcode?: string;
}

export interface BoxGenerationAction {
  type: 'generate';
  selectedItemIds: string[];
  description?: string;
}

export type TransferAction = ReallocateAction | BoxGenerationAction;

// Source type enumeration for better type safety
export enum SourceType {
  StorageUnit = 1,
  Warehouse = 2,
  Client = 3,
  Supplier = 4
}

export enum DestinationType {
  StorageUnit = 1,
  Warehouse = 2,
  Client = 3,
  Supplier = 4
}

// Priority levels
export enum TransferPriority {
  Low = 1,
  Normal = 2,
  High = 3,
  Urgent = 4
}

// Drag and drop interfaces
export interface DragDropItem {
  id: string;
  type: 'product' | 'box';
  barcode: string;
  quantity: number;
  sourceContainer?: string;
}

export interface DropZone {
  id: string;
  type: 'box' | 'standalone';
  accepts: ('product' | 'box')[];
  maxItems?: number;
}

// Box modification tracking
export interface BoxModification {
  boxId: string;
  originalBarcode: string;
  newBarcode: string;
  modificationType: 'items_added' | 'items_removed' | 'quantity_changed';
  timestamp: Date;
  changes: BoxItemChange[];
}

export interface BoxItemChange {
  itemBarcode: string;
  changeType: 'added' | 'removed' | 'quantity_updated';
  oldQuantity?: number;
  newQuantity?: number;
}

// New Movement Types for the updated API response
export interface MovementRecord {
  id: string;
  unitId: string;
  quantity: number;
  sourceType: number;
  sourceId: string;
  destinationType: number;
  destinationId: string;
  boxRefNumber: string;
  timestamp: string;
  itemName: string;
  unitBarcode: string;
  sourceName: string;
  destinationName: string;
  operationId: string;
  operationType: number;
}

export interface MovementApiResponse {
  items: MovementRecord[];
  itemsDictionary: any | null;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Enums for movement types
export enum MovementDirection {
  In = 1,
  Out = 2
}

export enum TransferType {
  MoveIn = 1,
  MoveOut = 2,
  Reallocation = 3,
  Shipment = 4,
  Transfer = 5
}

// Keep MovementType for backward compatibility
export enum MovementType {
  MoveIn = 1,
  MoveOut = 2,
  Reallocation = 3,
  Shipment = 4,
  Transfer = 5
}

export enum LocationType {
  StorageUnit = 1,
  Shipment = 2,
  Supplier = 3,
  Client = 4
}